import Breadcrumb, { Crumb } from "../../../components/common/Breadcrumbs";
import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import { OptionType } from "../../../helpers/utils/StyleUtils";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCreateStateMutation, useGetStateByIdQuery, useUpdateStateMutation } from "./statesApi";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import ButtonTray from "../../../components/common/form/ButtonTray";
import { Routes as AppRoutes } from "../../../routes";
import { createOptionListForSelectTag } from "../../../helpers/utils/Utils";
import SelectField from "../../../components/common/form/SelectField";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { getEnumDisplayNameByConstant } from "../../../helpers/utils/EnumUtils";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";

interface props {
    crumbs: Crumb[];
    title: string,
    btnText: string,
    isReadOnly?: boolean
}

const StateForm: FC<props> = ({ crumbs, title, btnText, isReadOnly }) => {

    const navigate = useNavigate();
    let { stateId } = useParams();

    const [stateData, setStateData] = useState<any>({});
    const stateByIdApi = useGetStateByIdQuery(stateId ?? skipToken);
    const [createState, createStateApiResponse] = useCreateStateMutation();
    const [updateState, updateStateApiResponse] = useUpdateStateMutation();
    const { data: enums } = useGetEnumsQuery("");


    useEffect(() => {
        if (stateByIdApi?.data) {
            setStateData({
                ...stateByIdApi?.data,
                productResultSchema: stateByIdApi?.data?.productResultSchema ? {
                    value: stateByIdApi?.data?.productResultSchema,
                    label: getEnumDisplayNameByConstant(enums?.["ProductResultSchema"], stateByIdApi?.data?.productResultSchema)
                } : null,
            });
        }
    }, [stateByIdApi?.data]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const commonObj = {
            ...stateData,
            productResultSchema: stateData?.productResultSchema?.value,
        };
        const createOrUpdateState = async () => {
            if (btnText === "Add") {
                return await createState(commonObj).unwrap();
            } else {
                return await updateState({
                    id: stateId,
                    requestBody: commonObj,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateState, (response: any) => {
            if (response?.success || response?.data?.success) {
                setStateData({});
                navigate("/states");
            }
        });
    }

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setStateData({
            ...stateData,
            [name]: value
        })
    }

    return (
        <>
            <div className="px-4 py-2">
                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs} />
                    {hasPermission(["AUDIT-V"]) &&
                        <AuditableInfoButton obj={stateByIdApi?.data} />}
                </div>
                <div className="flex justify-center mt-3">
                    <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={
                            <>
                                <div className="grid grid-cols-12">

                                    <InputField
                                        name={"fipsCode"}
                                        label="FIPS Code"
                                        onChange={handleOnChange}
                                        value={stateData?.fipsCode || ""}
                                        type="number"
                                        placeholder="Enter FIPS Code..."
                                        required={true}
                                        disabled={isReadOnly}
                                    />
                                    <InputField
                                        name={"stateName"}
                                        label="Name"
                                        onChange={handleOnChange}
                                        value={stateData?.stateName || ""}
                                        type="text"
                                        placeholder="Enter Name..."
                                        required={true}
                                        disabled={isReadOnly}
                                    />

                                    <InputField
                                        name={"stateAbbreviation"}
                                        label="State Abbreviation"
                                        onChange={handleOnChange}
                                        value={stateData?.stateAbbreviation || ""}
                                        type="text"
                                        placeholder="Enter State Abbreviation..."
                                        required={true}
                                        disabled={isReadOnly}
                                    />

                                    <InputField
                                        name={"taxRateDivisor"}
                                        label="Tax Rate Divisor"
                                        onChange={handleOnChange}
                                        value={stateData?.taxRateDivisor || ""}
                                        type="number"
                                        placeholder="Enter Tax Rate Divisor..."
                                        disabled={isReadOnly}
                                    />

                                    <SelectField
                                        placeholder='Select Product Result Schema...'
                                        value={stateData?.productResultSchema || null}
                                        options={createOptionListForSelectTag(enums?.["ProductResultSchema"], "value", "constant")}
                                        onChange={(selectedOption) => {
                                            handleOnChange({ name: "productResultSchema", value: selectedOption })
                                        }}
                                        label={"Product Result Schema"}
                                        required={true}
                                        isDisabled={isReadOnly}
                                    />
                                </div>

                                {!isReadOnly &&
                                    <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                        <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                        </div>
                                        <div className="relative col-span-9">
                                            <div className={"mt-4 flex"}>
                                                <ButtonTray
                                                    buttons={[
                                                        {
                                                            buttonProps: {
                                                                btnText: "Cancel",
                                                                onClick: () => navigate(AppRoutes.States.path),
                                                                type: "cancel",
                                                            },
                                                            buttonType: "button"
                                                        },
                                                        {
                                                            buttonProps: {
                                                                btnText: "Submit",
                                                                type: "submit",
                                                                isLoading: (createStateApiResponse.isLoading || updateStateApiResponse.isLoading),

                                                            },
                                                            buttonType: "button",

                                                        }
                                                    ]}
                                                    align="end"
                                                    gap={2}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                }


                            </>
                        }
                        />
                    </div>
                </div>


            </div>
        </>
    )
}
export default StateForm;